/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import { IViewModel, CmdBuilder } from "@nuance/hap-components";
import { BaseCommands, FormState, FormErrors } from "../../../State";
import { IEnvPanelData } from "../../EnvironmentSettings/EnvironmentViewModels";
import { ISettingGroupDefinition } from "@nuance/hierarchical-settings-ui-domain";
export interface IEnvPanelState extends FormState<IEnvPanelData> {
    definition: ISettingGroupDefinition;
}

export class EnvironmentPanelCommands extends BaseCommands<IEnvPanelData, IEnvPanelState> {
    constructor() {
        super();
        const cb = new CmdBuilder();
        const cmds = {
            onBlur: cb.bindCmd(this.OnBlurHandler),
            onTextChanged: cb.bindCmd(this.OnTextChangedHandler),
            onSettingNameChange: cb.bindCmd(this.onSettingNameChangeHandler)
        };

        this.Cmds = cmds;
        this.Reducer = cb.buildReducer<IEnvPanelState>(cmds);
    }

    readonly Cmds;
    readonly Reducer;

    /**
     * Validates the form fields.
     *
     * @param state Current state (immutable)
     * @returns Form errors
     */
    Validate(state: IEnvPanelState): FormErrors<IEnvPanelData> {
        const errors: FormErrors<IEnvPanelData> = {};

        if (state.data.settingName === "") {
            errors.settingName = "Setting name is required.";
        }
        if (state.data.defaultValue === "") {
            errors.defaultValue = "Default value is required.";
        }
        if (state.data.environment === "") {
            errors.environment = "Environment is required.";
        } else if (state.data.environment !== state.data.originalEnvironment) {
            // name/env must be unique
            const found = state.definition.EnvironmentalDefaultsOverride.find(
                override =>
                    override.SettingName === state.data.settingName &&
                    override.Environment === state.data.environment
            );
            if (found) {
                errors.environment =
                    "The combination of setting name & environment must be unique.";
            }
        }

        return errors;
    }

    onSettingNameChangeHandler = (state: IEnvPanelState, settingName: string) => {
        state.data.settingName = settingName;
        state.data.defaultValue =
            state.definition.Fields.find(
                def => def.SettingName === settingName
            )?.DefaultValue.toString() ?? "";

        state.isDirty = true;
        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);

        return state;
    };
}

type IEnvPanelCmds = EnvironmentPanelCommands["Cmds"];
export type IEnvPanelViewModel = IViewModel<IEnvPanelState, IEnvPanelCmds>;
